import React from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Alert,
  AlertTitle,
} from '@mui/material';

const Step7Review = ({ form, onEdit, onSubmit }) => {
  // Fungsi untuk mendapatkan data pendidikan terakhir dari riwayat pendidikan
  const getLastEducation = () => {
    if (!form.education || !Array.isArray(form.education) || form.education.length === 0) {
      return {
        institution: '-',
        major: '-',
        graduationYear: '-',
        level: '-'
      };
    }
    
    // Urutkan berdasarkan tahun (ambil yang terakhir)
    const sortedEducation = [...form.education].sort((a, b) => {
      const getYear = (period) => {
        if (!period) return 0;
        const years = period.split('-');
        return parseInt(years[years.length - 1]) || 0;
      };
      return getYear(b.period) - getYear(a.period);
    });
    
    const lastEdu = sortedEducation[0];
    return {
      institution: lastEdu.schoolName || '-',
      major: lastEdu.major || '-',
      graduationYear: lastEdu.period ? lastEdu.period.split('-')[1] || lastEdu.period : '-',
      level: lastEdu.certificate || '-'
    };
  };
  
  const lastEducation = getLastEducation();
  const formatEducation = () => {
    if (!form.education || form.education.length === 0) return '-';
    return form.education.map((edu, idx) => (
      <Typography variant="body2" key={idx} sx={{ mb: 0.5 }}>
        <strong>{edu.schoolName || '-'}</strong> - {edu.major || '-'} ({edu.period || '-'})
      </Typography>
    ));
  };

  const formatTraining = () => {
    if (!form.courses || form.courses.length === 0) return '-';
    return form.courses.map((course, idx) => (
      <Typography variant="body2" key={idx} sx={{ mb: 0.5 }}>
        <strong>{course.courseName || '-'}</strong> - {course.organizer || '-'} ({course.period || '-'})
        {course.certificate ? ` - Sertifikat: ${course.certificate}` : ''}
      </Typography>
    ));
  };

  const formatLanguages = () => {
    if (!form.languages || form.languages.length === 0) return '-';
    const label = (c) => ({ S: 'Sedang', CB: 'Cukup Baik', B: 'Baik', SB: 'Sangat Baik' }[c] || (c || '-'));
    
    return (
      <Box sx={{ width: '100%' }}>
        <Grid container spacing={2}>
          {form.languages.map((lang, idx) => (
            <Grid item xs={12} key={idx}>
              <Box sx={{ 
                backgroundColor: '#f8f9fa', 
                p: 2, 
                borderRadius: 1, 
                border: '1px solid #e0e0e0',
                mb: 1
              }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: '#1976d2' }}>
                  {lang.language || '-'}
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Kemampuan Tertulis:</strong> {label(lang.written)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Kemampuan Lisan:</strong> {label(lang.oral)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  const getYesNo = (value) => {
    if (!value) return '-';
    return value === 'ya' ? 'Ya' : 'Tidak';
  };

  // Format number with Indonesian thousands separator (.)
  const formatNumberID = (v) => {
    if (v === null || v === undefined || v === '') return '-';
    const digits = String(v).replace(/[^\d]/g, '');
    if (!digits) return '-';
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };


  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', my: 4, p: 2 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          backgroundColor: '#ffffff', 
          mb: 3,
          border: '2px solid #1976d2',
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(25, 118, 210, 0.15)'
        }}
      >
        <Grid container spacing={3}>
          {/* Header Section */}
          <Grid item xs={12}>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 3,
                pb: 2,
                borderBottom: '2px solid #e3f2fd'
              }}
            >
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 'bold', 
                  color: '#1976d2',
                  fontSize: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <span>✅</span>
                Review Data Pendaftaran
              </Typography>
            </Box>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#666', 
                mb: 3,
                fontStyle: 'italic',
                fontSize: '0.95rem'
              }}
            >
              Silakan periksa kembali data yang Anda masukkan sebelum mengirimkan pendaftaran. Klik tombol "Edit" untuk memperbaiki data jika diperlukan.
            </Typography>
          </Grid>

          {/* Warning Alert */}
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>Penting!</AlertTitle>
              Pastikan semua data yang Anda masukkan sudah benar dan lengkap. Data yang telah dikirim tidak dapat diubah kecuali melalui proses update data.
            </Alert>
          </Grid>

          {/* Personal Data */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#495057' }}>
                  Data Pribadi
                </Typography>
                <Button 
                  size="small" 
                  variant="outlined" 
                  onClick={() => onEdit(0)}
                  color="primary"
                >
                  Edit
                </Button>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Nama Lengkap:</strong> {form.fullName || '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Nama Panggilan:</strong> {form.nickname || '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Tempat, Tanggal Lahir:</strong> {form.birthPlace || '-'}, {form.birthDate || '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Kewarganegaraan/Suku Bangsa:</strong> {form.nationality || '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Agama:</strong> {form.religion || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Status Pernikahan:</strong> {form.maritalStatus || '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Jenis Kelamin:</strong> {form.gender || '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Golongan Darah:</strong> {form.bloodType || '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Buta Warna:</strong> {form.colorBlind || '-' }
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Tinggi Badan:</strong> {form.heightCm || '-'} cm
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Berat Badan:</strong> {form.weightKg || '-'} kg
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Alamat KTP:</strong> {form.addressKtp || '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Alamat Domisili:</strong> {form.addressCurrent || '-'}
                  </Typography>
                  {/* <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Kode Pos:</strong> {form.postalCode || '-'}
                  </Typography> */}
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>No. Telepon:</strong> {form.phone || '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Email:</strong> {form.email || '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Education Data */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#495057' }}>
                  Data Pendidikan
                </Typography>
                <Button 
                  size="small" 
                  variant="outlined" 
                  onClick={() => onEdit(1)}
                  color="primary"
                >
                  Edit
                </Button>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Riwayat Pendidikan:</Typography>
                  {formatEducation()}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Kursus / Pelatihan yang pernah dan sedang diikuti:</Typography>
                  {formatTraining()}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Pendidikan Terakhir:</strong> {lastEducation.level}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Jurusan:</strong> {lastEducation.major}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Institusi:</strong> {lastEducation.institution}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Tahun Lulus:</strong> {lastEducation.graduationYear}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Work Experience Data */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#495057' }}>
                  Data Pengalaman Kerja
                </Typography>
                <Button 
                  size="small" 
                  variant="outlined" 
                  onClick={() => onEdit(2)}
                  color="primary"
                >
                  Edit
                </Button>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Riwayat Pekerjaan:</Typography>
                  {form.workHistory && form.workHistory.length > 0 ? (
                    form.workHistory.map((work, idx) => (
                      <Box key={idx} sx={{ mb: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                          {idx + 1}. {work.companyName || '-'}
                        </Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              <strong>Tahun Kerja:</strong> {work.period || '-'}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              <strong>Jabatan:</strong> {work.position || '-'}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              <strong>Gaji Bersih:</strong> Rp {formatNumberID(work.basicSalary)}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              <strong>Tunjangan:</strong> Rp {formatNumberID(work.allowance)}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              <strong>Alasan Pindah:</strong> {work.movingReason || '-'}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              <strong>Uraian Pekerjaan:</strong> {work.jobDescription || '-'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    ))
                  ) : '-'}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Hobby:</strong> {form.hobby || '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Self Positive Points:</strong> {form.selfPositivePoints || '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Self Weakness Points:</strong> {form.selfWeaknessPoints || '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Perubahan/Pembaharuan yang dilakukan:</strong> {form.innovationDescription || '-'}
                  </Typography>
                </Grid>
                {/* <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Uraian Pekerjaan 1:</strong> {getJobDescriptions()[0]}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Uraian Pekerjaan 2:</strong> {getJobDescriptions()[1]}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Uraian Pekerjaan 3:</strong> {getJobDescriptions()[2]}
                  </Typography>
                </Grid> */}

                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Referensi Pekerjaan:</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Referensi 1:</Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Nama:</strong> {form.reference1Name || '-'}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Jabatan/Perusahaan:</strong> {form.reference1Position || '-'}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>No. Telepon:</strong> {form.reference1Phone || '-'}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Email:</strong> {form.reference1Email || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Referensi 2:</Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Nama:</strong> {form.reference2Name || '-'}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Jabatan/Perusahaan:</strong> {form.reference2Position || '-'}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>No. Telepon:</strong> {form.reference2Phone || '-'}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Email:</strong> {form.reference2Email || '-'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Penghargaan:</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>1.</strong> {form.award1 || '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>2.</strong> {form.award2 || '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>3.</strong> {form.award3 || '-'}
                  </Typography>
                </Grid>
                {/* <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Gaji yang diharapkan:</strong> {formatNumberID(form.expectedSalary)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Tunjangan yang diharapkan:</strong> {form.expectedBenefits || '-'}
                  </Typography>
                </Grid> */}
              </Grid>
            </Paper>
          </Grid>

          {/* Language Data */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#495057' }}>
                  Data Bahasa
                </Typography>
                <Button 
                  size="small" 
                  variant="outlined" 
                  onClick={() => onEdit(3)}
                  color="primary"
                >
                  Edit
                </Button>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Kemampuan Bahasa:</Typography>
                  {formatLanguages()}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Kemampuan Komunikasi:</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Kemampuan Presentasi:</strong> {form.presentationSkills || '-'}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Kemampuan Negosiasi:</strong> {form.negotiationSkills || '-'}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Kemampuan Menulis:</strong> {form.writingSkills || '-'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Salary Data */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#495057' }}>
                  Data Gaji & Tunjangan
                </Typography>
                <Button 
                  size="small" 
                  variant="outlined" 
                  onClick={() => onEdit(4)}
                  color="primary"
                >
                  Edit
                </Button>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Berapa besar gaji yang anda harapkan dari Perusahaan?</strong><br />
                    <span style={{ marginLeft: '20px' }}>{formatNumberID(form.expectedSalary)}</span>
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Selain gaji, kondisi kerja / tunjangan dan fasilitas apa yang saudara inginkan?</strong><br />
                    <span style={{ marginLeft: '20px' }}>{form.benefitsWanted || '-'}</span>
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Additional Questions */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#495057' }}>
                  Pertanyaan Tambahan
                </Typography>
                <Button 
                  size="small" 
                  variant="outlined" 
                  onClick={() => onEdit(5)}
                  color="primary"
                >
                  Edit
                </Button>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Izin hubungi lembaga/perusahaan:</strong> {getYesNo(form.allowContactInstitution)}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Keluarga/teman di perusahaan:</strong> {getYesNo(form.hasRelativesInCompany)}
                  </Typography>
                  {form.hasRelativesInCompany === 'ya' && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Info keluarga/teman:</strong> {form.relativesInfo || '-'}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Cacat jasmani:</strong> {getYesNo(form.hasPhysicalDisabilities)}
                  </Typography>
                  {form.hasPhysicalDisabilities === 'ya' && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Info cacat jasmani:</strong> {form.disabilitiesInfo || '-'}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Sakit keras/kecelakaan:</strong> {getYesNo(form.hasSeriousIllness)}
                  </Typography>
                  {form.hasSeriousIllness === 'ya' && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Info sakit/kecelakaan:</strong> {form.illnessInfo || '-'}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Rencana melanjutkan pendidikan:</strong> {getYesNo(form.planContinueEducation)}
                  </Typography>
                  {form.planContinueEducation === 'ya' && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Info rencana pendidikan:</strong> {form.educationPlanInfo || '-'}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Kapan siap bekerja:</strong> {form.whenCanStart || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Alasan ingin bergabung:</strong> {form.whyJoinCompany || '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Bersedia kerja di luar keahlian:</strong> {getYesNo(form.willingWorkBeyondExpertise)}
                  </Typography>
                  {form.willingWorkBeyondExpertise === 'tidak' && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Alasan tidak bersedia:</strong> {form.beyondExpertiseReason || '-'}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Dokumen Ringkas */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#495057' }}>
                  Dokumen Terupload (Ringkas)
                </Typography>
                <Button 
                  size="small" 
                  variant="outlined" 
                  onClick={() => onEdit(6)}
                  color="primary"
                >
                  Edit
                </Button>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Pas Foto:</strong> {Array.isArray(form.documents) && form.documents.find(d => d.type === 'pas_foto') ? 'Sudah diupload' : '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Ijazah Terakhir:</strong> {Array.isArray(form.documents) && form.documents.find(d => d.type === 'ijazah') ? 'Sudah diupload' : '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>CV:</strong> {Array.isArray(form.documents) && form.documents.find(d => d.type === 'cv') ? 'Sudah diupload' : '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Dokumen Tambahan:</strong> {Array.isArray(form.documents) ? form.documents.filter(d => d.type === 'additional').length : 0} file
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button 
                variant="outlined" 
                onClick={() => onEdit(5)}
                size="large"
                sx={{ minWidth: 120 }}
              >
                Kembali
              </Button>
              <Button 
                variant="contained" 
                onClick={onSubmit}
                size="large"
                sx={{ 
                  minWidth: 150,
                  backgroundColor: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                  }
                }}
              >
                Kirim Pendaftaran
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default Step7Review;
